import React from "react";
import { IPTOBankBalanceDTO } from "@interfaces/IPTOBankBalanceDTO";
import { LuEye } from "react-icons/lu";
import { useTranslation } from "react-i18next";

interface PTOBankBalanceProps {
  balance: IPTOBankBalanceDTO;
  isLoading?: boolean;
  error?: string | null;
  onViewRequests?: (requestType: "vacation" | "sick" | "all") => void;
  vacationRequestCount?: number;
  sickRequestCount?: number;
  totalRequestCount?: number;
  className?: string;
}

export const PTOBankBalance: React.FC<PTOBankBalanceProps> = ({
  balance,
  isLoading = false,
  error = null,
  onViewRequests,
  vacationRequestCount = 0,
  sickRequestCount = 0,
  totalRequestCount = 0,
  className = "",
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div
        className={`tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4 tw-mb-4 ${className}`}
      >
        <div className="tw-animate-pulse">
          <div className="tw-h-4 tw-bg-blue-200 tw-rounded tw-w-3/4 tw-mb-3"></div>
          <div className="tw-h-3 tw-bg-blue-200 tw-rounded tw-w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mb-4 ${className}`}
      >
        <p className="tw-text-red-700 tw-text-sm tw-font-medium">
          {t("ptoBankBalance.errorLoading")}
        </p>
        <p className="tw-text-red-600 tw-text-xs tw-mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div
      className={`tw-bg-gradient-to-br tw-from-blue-50 tw-to-indigo-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4 tw-mb-4 ${className}`}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700">
          {t("ptoBankBalance.title")} ({balance.year})
        </h3>
        {/* <span className="tw-text-xs tw-bg-blue-100 tw-text-blue-700 tw-px-2 tw-py-1 tw-rounded">
          {t("ptoBankBalance.workerId")}: {balance.worker_id}
        </span> */}
      </div>

      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
        {/* Vacation Balance */}
        <div className="tw-bg-white tw-rounded-md tw-p-3 tw-border tw-border-blue-100 tw-shadow-sm">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
            <span className="tw-text-xs tw-font-medium tw-text-gray-600">
              {t("ptoBankBalance.vacation")}
            </span>
            <span className="tw-text-xs tw-text-gray-500">
              {t("ptoBankBalance.of")}{" "}
              {balance.vacation.annual_entitlement.toFixed(2)}h
            </span>
          </div>
          <div className="tw-text-2xl tw-font-bold tw-text-blue-600">
            {balance.vacation.balance.toFixed(2)}
            <span className="tw-text-sm tw-font-normal tw-text-gray-500">
              {" "}
              {t("ptoBankBalance.hours")}
            </span>
          </div>
          <div className="tw-mt-2 tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-1.5">
            <div
              className="tw-bg-blue-500 tw-h-1.5 tw-rounded-full"
              style={{
                width: `${Math.min(
                  (balance.vacation.balance /
                    balance.vacation.annual_entitlement) *
                    100,
                  100
                )}%`,
              }}
            ></div>
          </div>
        </div>

        {/* Sick Balance */}
        <div className="tw-bg-white tw-rounded-md tw-p-3 tw-border tw-border-green-100 tw-shadow-sm">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
            <span className="tw-text-xs tw-font-medium tw-text-gray-600">
              {t("ptoBankBalance.sick")}
            </span>
            <span className="tw-text-xs tw-text-gray-500">
              {t("ptoBankBalance.of")}{" "}
              {balance.sick.annual_entitlement.toFixed(2)}h
            </span>
          </div>
          <div className="tw-text-2xl tw-font-bold tw-text-green-600">
            {balance.sick.balance.toFixed(2)}
            <span className="tw-text-sm tw-font-normal tw-text-gray-500">
              {" "}
              {t("ptoBankBalance.hours")}
            </span>
          </div>
          <div className="tw-mt-2 tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-1.5">
            <div
              className="tw-bg-green-500 tw-h-1.5 tw-rounded-full"
              style={{
                width: `${Math.min(
                  (balance.sick.balance / balance.sick.annual_entitlement) *
                    100,
                  100
                )}%`,
              }}
            ></div>
          </div>
        </div>

        {/* Total Balance */}
        <div className="tw-bg-white tw-rounded-md tw-p-3 tw-border tw-border-purple-100 tw-shadow-sm">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
            <span className="tw-text-xs tw-font-medium tw-text-gray-600">
              {t("ptoBankBalance.total")}
            </span>
            <span className="tw-text-xs tw-text-gray-500">
              {t("ptoBankBalance.of")}{" "}
              {balance.total.annual_entitlement.toFixed(2)}h
            </span>
          </div>
          <div className="tw-text-2xl tw-font-bold tw-text-purple-600">
            {balance.total.balance.toFixed(2)}
            <span className="tw-text-sm tw-font-normal tw-text-gray-500">
              {" "}
              {t("ptoBankBalance.hours")}
            </span>
          </div>
          <div className="tw-mt-2 tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-1.5">
            <div
              className="tw-bg-purple-500 tw-h-1.5 tw-rounded-full"
              style={{
                width: `${Math.min(
                  (balance.total.balance / balance.total.annual_entitlement) *
                    100,
                  100
                )}%`,
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Usage Summary */}
      <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-blue-100">
        <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-text-xs">
          <div className="tw-text-center">
            <p className="tw-text-gray-500 tw-mb-1">
              {t("ptoBankBalance.vacationUsed")}
            </p>
            <p className="tw-font-semibold tw-text-gray-700 tw-mb-2">
              {(
                balance.vacation.annual_entitlement - balance.vacation.balance
              ).toFixed(2)}
              h
            </p>
            {onViewRequests && (
              <button
                onClick={() => onViewRequests("vacation")}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-underline"
              >
                <LuEye size={12} />
                {t("ptoBankBalance.viewRequests")} ({vacationRequestCount})
              </button>
            )}
          </div>
          <div className="tw-text-center">
            <p className="tw-text-gray-500 tw-mb-1">
              {t("ptoBankBalance.sickUsed")}
            </p>
            <p className="tw-font-semibold tw-text-gray-700 tw-mb-2">
              {(balance.sick.annual_entitlement - balance.sick.balance).toFixed(
                2
              )}
              h
            </p>
            {onViewRequests && (
              <button
                onClick={() => onViewRequests("sick")}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-green-600 hover:tw-text-green-800 tw-underline"
              >
                <LuEye size={12} />
                {t("ptoBankBalance.viewRequests")} ({sickRequestCount})
              </button>
            )}
          </div>
          <div className="tw-text-center">
            <p className="tw-text-gray-500 tw-mb-1">
              {t("ptoBankBalance.totalUsed")}
            </p>
            <p className="tw-font-semibold tw-text-gray-700 tw-mb-2">
              {(
                balance.total.annual_entitlement - balance.total.balance
              ).toFixed(2)}
              h
            </p>
            {onViewRequests && (
              <button
                onClick={() => onViewRequests("all")}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-purple-600 hover:tw-text-purple-800 tw-underline"
              >
                <LuEye size={12} />
                {t("ptoBankBalance.viewAll")} ({totalRequestCount})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PTOBankBalance;
